import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useApiClient } from '../contexts/ApiContext';
import { queryKeys } from '../lib/queryClient';
import {
  SortByEnum,
  TypeEnum,
  WearEnum,
  type GetShopSkinsSortBy,
  type ShopControllerGetShopItemsParams,
  ShopControllerGetShopItemsParams1SortByEnum,
} from '../services/generated';

const ITEMS_PER_PAGE = 20;

export interface ShopItemsFilter {
  weapon?: string;
  skinName?: string;
  category?: string;
  type?: string;
  wear?: string;
  rarity?: string;
  minPrice?: string;
  maxPrice?: string;
  statTrak?: boolean;
  sortBy?: ShopControllerGetShopItemsParams1SortByEnum;
}

const VALID_RARITIES_BY_TYPE: Record<string, string[]> = {
  '': [
    'Consumer Grade',
    'Industrial Grade',
    'Mil-Spec Grade',
    'Restricted',
    'Classified',
    'Covert',
    'Exotic',
    'Remarkable',
    'Extraordinary',
    'High Grade',
    'Base Grade',
    'Distinguished',
    'Exceptional',
    'Superior',
    'Master',
  ],
  skin: ['Consumer Grade', 'Industrial Grade', 'Mil-Spec Grade', 'Restricted', 'Classified', 'Covert', 'Extraordinary'],
  crate: ['Base Grade'],
  sticker: ['High Grade', 'Remarkable', 'Exotic', 'Extraordinary'],
  keychain: ['Remarkable', 'Exotic', 'Extraordinary'],
  agent: ['Distinguished', 'Exceptional', 'Superior', 'Master'],
  graffiti: ['High Grade'],
};

const SHOP_TYPES = Object.values(TypeEnum);
const SHOP_WEARS = Object.values(WearEnum);
const SORT_BY: Record<ShopControllerGetShopItemsParams1SortByEnum, SortByEnum> = {
  [ShopControllerGetShopItemsParams1SortByEnum.VolumeDesc]: SortByEnum.VolumeDesc,
  [ShopControllerGetShopItemsParams1SortByEnum.VolumeAsc]: SortByEnum.VolumeAsc,
  [ShopControllerGetShopItemsParams1SortByEnum.PriceDesc]: SortByEnum.PriceDesc,
  [ShopControllerGetShopItemsParams1SortByEnum.PriceAsc]: SortByEnum.PriceAsc,
};

function buildFilterParams(filters: ShopItemsFilter, page: number): ShopControllerGetShopItemsParams {
  const filterParams: ShopControllerGetShopItemsParams = {
    page,
    limit: ITEMS_PER_PAGE,
  };

  const isSkinType = !filters.type || filters.type === 'skin';

  if (filters.category && isSkinType) {
    filterParams.category = filters.category;
  }

  if (filters.weapon && isSkinType) {
    filterParams.weapon = filters.weapon;
  }

  if (filters.skinName && isSkinType) {
    filterParams.skinName = filters.skinName;
  }

  if (filters.type) {
    filterParams.type = SHOP_TYPES.find((candidate) => candidate === filters.type);
  }

  if (filters.wear && isSkinType) {
    filterParams.wear = SHOP_WEARS.find((candidate) => candidate === filters.wear);
  }

  if (filters.rarity) {
    const validRarities = VALID_RARITIES_BY_TYPE[filters.type || ''] || VALID_RARITIES_BY_TYPE[''];
    if (validRarities.includes(filters.rarity)) {
      filterParams.rarity = filters.rarity;
    }
  }

  if (filters.minPrice) {
    filterParams.minPrice = Math.max(parseInt(filters.minPrice), 25000);
  }

  if (filters.maxPrice) {
    filterParams.maxPrice = Math.max(parseInt(filters.maxPrice), 25000);
  }

  if (filters.statTrak && isSkinType) {
    filterParams.statTrak = true;
  }

  if (filters.sortBy) {
    filterParams.sortBy = SORT_BY[filters.sortBy];
  }

  return filterParams;
}

export function useShopItemsInfinite(filters: ShopItemsFilter) {
  const apiClient = useApiClient();

  const filterKey = {
    weapon: filters.weapon,
    skinName: filters.skinName,
    category: filters.category,
    type: filters.type,
    wear: filters.wear,
    rarity: filters.rarity,
    minPrice: filters.minPrice,
    maxPrice: filters.maxPrice,
    statTrak: filters.statTrak,
    sortBy: filters.sortBy,
  };

  return useInfiniteQuery({
    queryKey: queryKeys.shopItems(filterKey),
    queryFn: async ({ pageParam = 1 }) => {
      const params = buildFilterParams(filters, pageParam);
      const response = await apiClient.api.shopControllerGetShopItems(params);
      return {
        items: response.items,
        meta: response.meta,
      };
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.meta.currentPage >= lastPage.meta.totalPages) return undefined;
      return lastPage.meta.currentPage + 1;
    },
    initialPageParam: 1,
  });
}

export type ShopSkinsFilter = {
  rarity?: string;
  sortBy?: GetShopSkinsSortBy;
};

export function useShopSkins(weapon: string, filters?: ShopSkinsFilter, enabled = true) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: queryKeys.shopSkins(weapon, filters),
    queryFn: async () => {
      const response = await apiClient.api.shopControllerGetShopSkins({
        weapon,
        rarity: filters?.rarity || undefined,
        sortBy: filters?.sortBy || undefined,
      });
      return response.skins;
    },
    enabled: enabled && !!weapon,
  });
}
